import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Download,
  FileCheck2,
  Loader2,
  Mail,
  Search,
  Send,
  Sparkles,
  UserRoundCheck,
  UsersRound
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import {
  CleanApprovalApiError,
  cleanApprovalPdfObjectUrl,
  cleanApprovalService,
  downloadCleanApprovalPdf
} from '@/services/cleanApprovalService';
import type {
  CleanApprovalConditionState,
  CleanApprovalPreview,
  CleanApprovalRecipients,
  CleanApprovalSendResult,
  CleanApprovalState
} from '@/types/clean-approval';

interface CleanApprovalSheetProps {
  loanId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type WorkflowStep = 'select' | 'preview' | 'sent';

function roleLabel(role: 'loan_officer' | 'processor'): string {
  return role === 'loan_officer' ? 'Loan officer' : 'Processor';
}

function timingLabel(priorTo: string): string {
  const normalized = priorTo.trim().toLowerCase();
  if (normalized === 'docs') {
    return 'PTD';
  }
  if (normalized === 'funding') {
    return 'PTF';
  }
  if (normalized === 'closing') {
    return 'At closing';
  }
  return priorTo || 'Uncategorized';
}

function formatTimestamp(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString();
}

function errorMessage(error: unknown): string {
  if (error instanceof CleanApprovalApiError) {
    if (error.code === 'LOAN_NOT_CONDITIONALLY_APPROVED') {
      return 'Clean Approval is available after the loan reaches conditional approval.';
    }
    if (error.code === 'HISTORICAL_LOAN_BLOCKED') {
      return 'Email delivery is blocked for this historical or excluded loan.';
    }
    if (error.code === 'DELIVERY_IN_PROGRESS') {
      return 'This delivery is already in progress. Wait a moment, then reload the Clean Approval.';
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Clean Approval could not be loaded.';
}

function RecipientSummary({ recipients }: { recipients: CleanApprovalRecipients }) {
  return (
    <div className="space-y-2">
      {recipients.all.length > 0 ? (
        recipients.all.map((recipient) => (
          <div key={`${recipient.role}-${recipient.email}`} className="flex items-start gap-2 text-sm">
            <UserRoundCheck className="mt-0.5 h-4 w-4 shrink-0 text-success" />
            <div className="min-w-0">
              <p className="font-medium text-foreground">
                {roleLabel(recipient.role)}: {recipient.name}
              </p>
              <p className="truncate text-muted-foreground">{recipient.email}</p>
            </div>
          </div>
        ))
      ) : (
        <p className="text-sm text-destructive">No active assigned recipient was found.</p>
      )}
      {recipients.ccEmails.map((email) => (
        <div key={`cc-${email}`} className="flex items-start gap-2 text-sm">
          <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="font-medium text-foreground">Fixed CC</p>
            <p className="truncate text-muted-foreground">{email}</p>
          </div>
        </div>
      ))}
      {recipients.missingRoles.map((role) => (
        <p key={role} className="text-xs text-warning">
          No active assigned {roleLabel(role).toLowerCase()} email was found. The other assigned recipient can still receive the PDF.
        </p>
      ))}
    </div>
  );
}

function StepIndicator({ step }: { step: WorkflowStep }) {
  const stages: Array<{ id: WorkflowStep; label: string }> = [
    { id: 'select', label: 'Select' },
    { id: 'preview', label: 'Preview' },
    { id: 'sent', label: 'Sent' }
  ];
  const activeIndex = stages.findIndex((stage) => stage.id === step);

  return (
    <div className="flex items-center gap-2" aria-label={`Clean Approval step: ${stages[activeIndex].label}`}>
      {stages.map((stage, index) => (
        <div key={stage.id} className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                index <= activeIndex
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              {index < activeIndex ? <Check className="h-3.5 w-3.5" /> : index + 1}
            </span>
            <span className={`hidden text-xs font-medium sm:inline ${index <= activeIndex ? 'text-foreground' : 'text-muted-foreground'}`}>
              {stage.label}
            </span>
          </div>
          {index < stages.length - 1 && <span className="h-px w-5 bg-border sm:w-8" />}
        </div>
      ))}
    </div>
  );
}

export function CleanApprovalSheet({ loanId, open, onOpenChange }: CleanApprovalSheetProps) {
  const { toast } = useToast();
  const [step, setStep] = useState<WorkflowStep>('select');
  const [state, setState] = useState<CleanApprovalState | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<CleanApprovalPreview | null>(null);
  const [sendResult, setSendResult] = useState<CleanApprovalSendResult | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const loadState = useCallback(async (preserveStep = false) => {
    setLoading(true);
    setError(null);
    try {
      const nextState = await cleanApprovalService.getState(loanId);
      setState(nextState);
      setSelectedIds(new Set(
        nextState.conditions
          .filter((condition) => condition.included)
          .map((condition) => condition.instanceId)
      ));
      if (!preserveStep) {
        setStep('select');
        setPreview(null);
        setSendResult(null);
        setNotice(null);
      }
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSearch('');
    setStep('select');
    setPreview(null);
    setSendResult(null);
    setNotice(null);
    void loadState();
  }, [open, loadState]);

  useEffect(() => {
    if (!preview?.pdfBase64) {
      setPdfUrl(null);
      return;
    }
    const url = cleanApprovalPdfObjectUrl(preview.pdfBase64);
    setPdfUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [preview?.pdfBase64]);

  const filteredConditions = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return state?.conditions || [];
    }
    return (state?.conditions || []).filter((condition) => [
      condition.title,
      condition.externalDescription,
      condition.externalId,
      condition.category,
      condition.priorTo
    ].some((value) => value?.toLowerCase().includes(query)));
  }, [search, state?.conditions]);

  const selectedCount = selectedIds.size;
  const automaticCount = state?.conditions.filter((condition) => condition.automaticMatch).length || 0;
  const lastSentAt = formatTimestamp(state?.lastSentAt);
  const sendRecipients = preview?.recipients || state?.recipients;
  const canSend = Boolean(preview && sendRecipients && sendRecipients.all.length > 0 && !sending);
  const sendVersionLabel = state?.lastSentAt ? 'Updated Clean Approval' : 'Initial Clean Approval';

  const toggleCondition = (condition: CleanApprovalConditionState, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(condition.instanceId);
      } else {
        next.delete(condition.instanceId);
      }
      return next;
    });
    setNotice(null);
  };

  const handleSaveAndPreview = async () => {
    if (!state || saving) {
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await cleanApprovalService.saveAndPreview(
        loanId,
        state.revision,
        Array.from(selectedIds)
      );
      const staleIds = new Set(result.removedStaleConditionInstanceIds || []);
      setPreview(result);
      setState((current) => current ? {
        ...current,
        revision: result.revision,
        conditions: current.conditions
          .filter((condition) => !staleIds.has(condition.instanceId))
          .map((condition) => ({
            ...condition,
            included: selectedIds.has(condition.instanceId)
          })),
        recipients: result.recipients,
        lastDelivery: result.lastDelivery,
        lastSavedAt: result.generatedAt,
        lastPreviewedAt: result.generatedAt
      } : current);
      setStep('preview');

      if (result.removedStaleConditionInstanceIds?.length) {
        setSelectedIds((current) => {
          const next = new Set(current);
          result.removedStaleConditionInstanceIds?.forEach((id) => next.delete(id));
          return next;
        });
        setNotice(`${result.removedStaleConditionInstanceIds.length} condition${result.removedStaleConditionInstanceIds.length === 1 ? '' : 's'} changed or closed before save and were removed from this PDF.`);
      }
      if (result.newlyReachedThresholds?.length) {
        toast({
          title: 'Automation candidate ready',
          description: `${result.newlyReachedThresholds.length} condition ID${result.newlyReachedThresholds.length === 1 ? ' has' : 's have'} reached 10 reviewed loans and can now be reviewed by an administrator.`
        });
      }
    } catch (saveError) {
      if (saveError instanceof CleanApprovalApiError && saveError.code === 'REVISION_CONFLICT') {
        await loadState();
        setNotice('Another user changed this Clean Approval. The latest selections have been reloaded.');
      } else if (
        saveError instanceof CleanApprovalApiError &&
        saveError.code === 'PREVIEW_RENDER_FAILED' &&
        saveError.details.saved &&
        typeof saveError.details.revision === 'number'
      ) {
        try {
          const result = await cleanApprovalService.previewCurrent(loanId, saveError.details.revision);
          setPreview(result);
          setState((current) => current ? { ...current, revision: result.revision, recipients: result.recipients } : current);
          setStep('preview');
          setNotice('Your selections were saved and the preview succeeded on retry.');
        } catch (previewError) {
          setError(errorMessage(previewError));
        }
      } else {
        setError(errorMessage(saveError));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!preview || sending) {
      return;
    }
    setConfirmOpen(false);
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const result = await cleanApprovalService.send(
        loanId,
        preview.revision,
        preview.previewFingerprint
      );
      setSendResult(result);
      setStep('sent');
      setState((current) => current ? {
        ...current,
        lastSentAt: result.sentAt || current.lastSentAt,
        recipients: result.recipients
      } : current);
    } catch (sendError) {
      if (sendError instanceof CleanApprovalApiError && sendError.code === 'PREVIEW_STALE') {
        try {
          const refreshed = await cleanApprovalService.previewCurrent(loanId, preview.revision);
          const rendererChanged = sendError.details.staleReason === 'renderer_changed';
          setPreview(refreshed);
          setNotice(rendererChanged
            ? 'The approval PDF layout changed after your preview. Review this refreshed PDF and confirm again before sending.'
            : 'The loan or selected condition text changed after your preview. Review this refreshed PDF and confirm again before sending.');
          toast({
            title: 'Preview refreshed',
            description: rendererChanged
              ? 'Nothing was sent. Review the PDF with the current layout, then confirm again.'
              : 'Nothing was sent. Review the updated PDF, then confirm again.'
          });
        } catch (refreshError) {
          setError(errorMessage(refreshError));
        }
      } else if (sendError instanceof CleanApprovalApiError && sendError.code === 'REVISION_CONFLICT') {
        await loadState();
        setNotice('Another user changed this Clean Approval. The latest selections have been reloaded.');
      } else {
        setError(errorMessage(sendError));
      }
    } finally {
      setSending(false);
    }
  };

  const resetForUpdate = async () => {
    setStep('select');
    setSendResult(null);
    setPreview(null);
    setNotice(null);
    await loadState(true);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-[92vw] xl:max-w-[1180px]">
          <SheetHeader className="shrink-0 border-b px-5 py-4 pr-12 sm:px-6">
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
              <div>
                <SheetTitle className="flex items-center gap-2">
                  <FileCheck2 className="h-5 w-5 text-primary" />
                  Clean Approval
                </SheetTitle>
                <SheetDescription>
                  Create a limited approval PDF for realtors or other outside parties. It is sent to the assigned loan officer and processor, with Anthony copied for delivery verification.
                </SheetDescription>
              </div>
              <StepIndicator step={step} />
            </div>
          </SheetHeader>

          {loading && !state ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                <p className="mt-3 text-sm text-muted-foreground">Loading current open conditions…</p>
              </div>
            </div>
          ) : error && !state ? (
            <div className="flex flex-1 items-center justify-center p-6">
              <Alert variant="destructive" className="max-w-xl">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Clean Approval unavailable</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>{error}</p>
                  <Button variant="outline" size="sm" onClick={() => void loadState()}>
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          ) : step === 'select' && state ? (
            <>
              <div className="flex min-h-0 flex-1 flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_300px]">
                <div className="flex min-h-0 flex-1 flex-col border-b lg:border-b-0 lg:border-r">
                  <div className="shrink-0 space-y-3 border-b bg-muted/20 px-5 py-4 sm:px-6">
                    <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
                      <div>
                        <h3 className="font-semibold">Choose outside-party conditions</h3>
                        <p className="text-sm text-muted-foreground">
                          Include only conditions the outside party needs to understand or help resolve.
                        </p>
                      </div>
                      <div className="text-sm font-medium">
                        {selectedCount} selected
                      </div>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search title, description, category, or condition ID"
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <ScrollArea className="min-h-0 flex-1">
                    <div className="space-y-2 p-4 sm:p-5">
                      {filteredConditions.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                          {state.conditions.length === 0
                            ? 'There are no open underwriting conditions. You can still create a zero-condition Clean Approval.'
                            : 'No conditions match this search.'}
                        </div>
                      ) : (
                        filteredConditions.map((condition, index) => {
                          const checkboxId = `clean-approval-${index}`;
                          const checked = selectedIds.has(condition.instanceId);
                          return (
                            <label
                              key={condition.instanceId}
                              htmlFor={checkboxId}
                              className={`block cursor-pointer rounded-lg border p-4 transition-colors ${
                                checked ? 'border-primary/50 bg-primary/5' : 'hover:bg-muted/40'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  id={checkboxId}
                                  checked={checked}
                                  onCheckedChange={(value) => toggleCondition(condition, value === true)}
                                  className="mt-0.5"
                                />
                                <div className="min-w-0 flex-1 space-y-2">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-medium leading-snug">{condition.title}</span>
                                    {condition.automaticMatch ? (
                                      <Badge className="gap-1 bg-success text-success-foreground">
                                        <Sparkles className="h-3 w-3" />
                                        Auto-included
                                      </Badge>
                                    ) : condition.externalId === null ? (
                                      <Badge variant="outline">Manual only</Badge>
                                    ) : null}
                                    {condition.category && <Badge variant="secondary">{condition.category}</Badge>}
                                    <Badge variant="outline">{timingLabel(condition.priorTo)}</Badge>
                                  </div>
                                  {condition.externalDescription ? (
                                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                                      {condition.externalDescription}
                                    </p>
                                  ) : (
                                    <p className="text-sm italic text-muted-foreground">
                                      No external description; the PDF will show the condition title only.
                                    </p>
                                  )}
                                  {condition.externalId && (
                                    <p className="text-xs text-muted-foreground">Condition ID {condition.externalId}</p>
                                  )}
                                </div>
                              </div>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </ScrollArea>
                </div>

                <ScrollArea className="max-h-[36vh] lg:max-h-none">
                  <aside className="space-y-5 p-5 sm:p-6">
                    <div>
                      <h3 className="flex items-center gap-2 font-semibold">
                        <UsersRound className="h-4 w-4 text-primary" />
                        Internal recipients
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Recipients cannot be edited. Staff decides whether and how to forward the PDF externally.
                      </p>
                    </div>
                    <RecipientSummary recipients={state.recipients} />
                    <Separator />
                    <div className="space-y-2 text-sm">
                      <p className="font-medium">Selection safeguards</p>
                      <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
                        <li>Only the external condition description is used.</li>
                        <li>Internal notes and condition status are excluded.</li>
                        <li>Zero conditions is a valid approval summary.</li>
                        <li>There is no bulk “select all” action.</li>
                      </ul>
                    </div>
                    {automaticCount > 0 && (
                      <Alert>
                        <Sparkles className="h-4 w-4" />
                        <AlertTitle>{automaticCount} learned match{automaticCount === 1 ? '' : 'es'}</AlertTitle>
                        <AlertDescription>
                          These were preselected from administrator-approved condition IDs. You can remove any of them before saving.
                        </AlertDescription>
                      </Alert>
                    )}
                    {lastSentAt && (
                      <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                        Last sent {lastSentAt}. Saving and sending again creates an updated Clean Approval.
                      </div>
                    )}
                  </aside>
                </ScrollArea>
              </div>

              {(error || notice) && (
                <div className="shrink-0 border-t px-5 py-3 sm:px-6">
                  <Alert variant={error ? 'destructive' : 'default'}>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error || notice}</AlertDescription>
                  </Alert>
                </div>
              )}

              <SheetFooter className="shrink-0 border-t bg-background px-5 py-4 sm:px-6">
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={() => void handleSaveAndPreview()} disabled={saving || loading}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileCheck2 className="mr-2 h-4 w-4" />}
                  Save & Preview PDF
                </Button>
              </SheetFooter>
            </>
          ) : step === 'preview' && preview ? (
            <>
              <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="min-h-[360px] border-b bg-muted/30 p-3 lg:min-h-0 lg:border-b-0 lg:border-r sm:p-5">
                  {pdfUrl ? (
                    <iframe
                      title="Clean Approval PDF preview"
                      src={pdfUrl}
                      className="h-full min-h-[340px] w-full rounded-md border bg-white shadow-sm"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <Loader2 className="h-7 w-7 animate-spin text-primary" />
                    </div>
                  )}
                </div>
                <ScrollArea className="max-h-[42vh] lg:max-h-none">
                  <aside className="space-y-5 p-5 sm:p-6">
                    <div>
                      <h3 className="font-semibold">Review before sending</h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Confirm the PDF is appropriate for an outside party. Nothing is sent until you confirm.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg border p-3">
                        <p className="text-xs text-muted-foreground">Conditions shown</p>
                        <p className="mt-1 text-xl font-semibold">{preview.conditionCount}</p>
                      </div>
                      <div className="rounded-lg border p-3">
                        <p className="text-xs text-muted-foreground">Draft revision</p>
                        <p className="mt-1 text-xl font-semibold">{preview.revision}</p>
                      </div>
                    </div>
                    <div>
                      <p className="mb-2 flex items-center gap-2 text-sm font-medium">
                        <Mail className="h-4 w-4 text-primary" />
                        Will be emailed to
                      </p>
                      <RecipientSummary recipients={preview.recipients} />
                    </div>
                    {(notice || preview.liveDriftDetected) && (
                      <Alert className="border-warning/50 bg-warning/10">
                        <AlertCircle className="h-4 w-4 text-warning" />
                        <AlertTitle>Review required</AlertTitle>
                        <AlertDescription>
                          {notice || 'Live condition text changed while this draft was open. This PDF reflects the newest available text.'}
                        </AlertDescription>
                      </Alert>
                    )}
                    {error && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    {preview.recipients.all.length === 0 && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          Sending is disabled until an active assigned loan officer or processor has a valid email address.
                        </AlertDescription>
                      </Alert>
                    )}
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => downloadCleanApprovalPdf(preview.pdfBase64, preview.filename)}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  </aside>
                </ScrollArea>
              </div>
              <SheetFooter className="shrink-0 border-t bg-background px-5 py-4 sm:px-6">
                <Button variant="outline" onClick={() => setStep('select')} disabled={sending}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Edit selections
                </Button>
                <Button onClick={() => setConfirmOpen(true)} disabled={!canSend}>
                  {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Send to loan team
                </Button>
              </SheetFooter>
            </>
          ) : step === 'sent' && sendResult ? (
            <div className="flex flex-1 items-center justify-center p-6">
              <div className="w-full max-w-xl text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success/15">
                  <CheckCircle2 className="h-9 w-9 text-success" />
                </span>
                <h3 className="mt-5 text-xl font-semibold">
                  {sendResult.alreadySent ? 'Clean Approval was already sent' : 'Clean Approval sent'}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {sendResult.alreadySent
                    ? 'This exact saved revision had already been delivered, so no duplicate email was created.'
                    : 'The PDF was emailed to the active assigned loan team and copied to Anthony. They can now review and distribute it to the appropriate outside party.'}
                </p>
                <div className="mt-6 rounded-lg border p-4 text-left">
                  <RecipientSummary recipients={sendResult.recipients} />
                  {sendResult.sentAt && (
                    <p className="mt-3 text-xs text-muted-foreground">Sent {formatTimestamp(sendResult.sentAt)}</p>
                  )}
                </div>
                <div className="mt-6 flex flex-col-reverse justify-center gap-2 sm:flex-row">
                  {preview && (
                    <Button
                      variant="outline"
                      onClick={() => downloadCleanApprovalPdf(preview.pdfBase64, preview.filename)}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download PDF
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => void resetForUpdate()}>
                    Create an updated version
                  </Button>
                  <Button onClick={() => onOpenChange(false)}>Done</Button>
                </div>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send this Clean Approval to the loan team?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends the reviewed PDF to the active assigned loan officer and processor, with anthony@lendwisemtg.com copied. It does not email a realtor or any other outside party.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {preview && (
            <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-lg border bg-muted/30 p-4 text-sm">
              <dt className="text-muted-foreground">Version</dt>
              <dd className="font-medium">{sendVersionLabel}</dd>
              <dt className="text-muted-foreground">Filename</dt>
              <dd className="break-all font-medium">{preview.filename}</dd>
              <dt className="text-muted-foreground">Conditions</dt>
              <dd className="font-medium">
                {preview.conditionCount} condition{preview.conditionCount === 1 ? '' : 's'}
              </dd>
            </dl>
          )}
          {sendRecipients && (
            <div className="rounded-lg border bg-muted/30 p-4">
              <RecipientSummary recipients={sendRecipients} />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sending}>Review again</AlertDialogCancel>
            <Button onClick={() => void handleSend()} disabled={!canSend}>
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Confirm & Send
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
